import { Datepicker } from '@mobiscroll/react';
import React, { useImperativeHandle, useRef } from 'react';
import { AsyncTypeahead, Typeahead } from 'react-bootstrap-typeahead';
import MaskedInput from 'react-maskedinput';
import { ReactSVG } from 'react-svg';

const Temp = MaskedInput as unknown as (props: any) => JSX.Element;

export const MaskedInputWrapper = React.forwardRef((props: any, ref: any) => {
  // Trích xuất các props cụ thể mà MaskedInput cần
  const {
    mask,
    name,
    id,
    placeholder,
    value,
    onChange,
    onBlur,
    className,
    disabled,
    required,
    autoComplete,
    'aria-describedby': ariaDescribedby,
    'data-private': dataPrivate,
    ...otherProps
  } = props;

  // Truyền các props một cách rõ ràng
  const renderResult = (
    <Temp
      ref={ref}
      mask={mask}
      name={name}
      id={id}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      className={className}
      disabled={disabled}
      required={required}
      autoComplete={autoComplete}
      aria-describedby={ariaDescribedby}
      data-private={dataPrivate}
      // Nếu cần thiết, thêm các props khác ở đây
    />
  );

  if (!renderResult || Object.keys(renderResult).length === 0) {
    return <></>; // Return Fragment if renderResult is empty
  }
  return renderResult;
});

// Tương tự cho các wrapper khác
const Temp2 = Datepicker as unknown as (props: any) => JSX.Element;
export const DatePickerWrapper = React.forwardRef((props: any, ref: any) => {
  const {
    value,
    onChange,
    onBlur,
    className,
    disabled,
    required,
    min,
    max,
    placeholder,
    ...otherProps
  } = props;

  const renderResult = (
    <Temp2
      ref={ref}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      className={className}
      disabled={disabled}
      required={required}
      min={min}
      max={max}
      placeholder={placeholder}
    />
  );

  if (!renderResult || Object.keys(renderResult).length === 0) {
    return <></>;
  }
  return renderResult;
});

const Temp3 = ReactSVG as unknown as (props: any) => JSX.Element;
export const ReactSVGWrapper = React.forwardRef((props: any, ref: any) => {
  const {
    src,
    className,
    wrapper,
    beforeInjection,
    afterInjection,
    ...otherProps
  } = props;

  const renderResult = (
    <Temp3
      ref={ref}
      src={src}
      className={className}
      wrapper={wrapper}
      beforeInjection={beforeInjection}
      afterInjection={afterInjection}
    />
  );

  if (!renderResult || Object.keys(renderResult).length === 0) {
    return <></>;
  }
  return renderResult;
});
const Temp4 = Typeahead as unknown as (props: any) => JSX.Element;
export const TypeaheadWrapper = React.forwardRef((props: any, ref: any) => {
  // Trích xuất các props cụ thể mà Typeahead cần
  const {
    id,
    options,
    selected,
    onChange,
    onBlur,
    className,
    disabled,
    placeholder,
    multiple,
    labelKey,
    filterBy,
    renderMenuItemChildren,
    minLength,
    inputProps,
    align,
    clearButton,
    emptyLabel,
    flip,
    highlightOnlyResult,
    isLoading,
    paginationText,
    ...otherProps
  } = props;

  // Truyền các props một cách rõ ràng
  const renderResult = (
    <Temp4
      ref={ref}
      id={id}
      options={options}
      selected={selected}
      onChange={onChange}
      onBlur={onBlur}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
      multiple={multiple}
      labelKey={labelKey}
      filterBy={filterBy}
      renderMenuItemChildren={renderMenuItemChildren}
      minLength={minLength}
      inputProps={inputProps}
      align={align}
      clearButton={clearButton}
      emptyLabel={emptyLabel}
      flip={flip}
      highlightOnlyResult={highlightOnlyResult}
      isLoading={isLoading}
      paginationText={paginationText}
    />
  );

  if (!renderResult || Object.keys(renderResult).length === 0) {
    return <></>; // Return Fragment if renderResult is empty
  }
  return renderResult;
});

const Temp6 = AsyncTypeahead as unknown as (props: any) => JSX.Element;
interface AsyncTypeaheadRefType {
  focus: () => void;
  getInput: () => HTMLInputElement | null;
}
export const AsyncTypeaheadWrapper = React.forwardRef<
  AsyncTypeaheadRefType,
  any
>((props: any, ref: React.Ref<AsyncTypeaheadRefType>) => {
  const innerRef = useRef<any>(null);

  // Expose các phương thức cho component cha
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (innerRef.current && typeof innerRef.current.focus === 'function') {
        innerRef.current.focus();
      }
    },
    getInput: () => {
      if (innerRef.current && typeof innerRef.current.getInput === 'function') {
        return innerRef.current.getInput();
      }
      return null;
    },
  }));

  const {
    id,
    options,
    selected,
    onChange,
    onBlur,
    onFocus,
    className,
    disabled,
    placeholder,
    multiple,
    labelKey,
    filterBy,
    renderMenuItemChildren,
    minLength = 0,
    inputProps,
    align,
    clearButton,
    emptyLabel,
    flip,
    highlightOnlyResult,
    isLoading,
    paginationText,
    useCache,
    onSearch,
    defaultInputValue,
    isValid,
    isInvalid,
    showMenu = true,
    ...otherProps
  } = props;

  const renderResult = (
    <Temp6
      ref={innerRef}
      id={id}
      options={options}
      selected={selected}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
      multiple={multiple}
      labelKey={labelKey}
      filterBy={filterBy}
      renderMenuItemChildren={renderMenuItemChildren}
      minLength={minLength}
      inputProps={inputProps}
      align={align}
      clearButton={clearButton}
      emptyLabel={emptyLabel}
      flip={flip}
      highlightOnlyResult={highlightOnlyResult}
      isLoading={isLoading}
      paginationText={paginationText}
      useCache={useCache}
      onSearch={onSearch}
      defaultInputValue={defaultInputValue}
      isValid={isValid}
      isInvalid={isInvalid}
      showMenu={showMenu}
      {...otherProps}
    />
  );

  if (!renderResult || Object.keys(renderResult).length === 0) {
    return <></>;
  }
  return renderResult;
});
